import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import {
  Plane, Sparkles, Globe, Users, FileText, BarChart3,
  Shield, Zap, ArrowRight, CheckCircle, MessageSquare,
  ChevronRight, Coins, Gift, Star, Brain, Image as ImageIcon
} from 'lucide-react';

const FEATURES = [
  { icon: Sparkles, title: 'AI Itinerary Builder', desc: 'Generate stunning itineraries from raw text, PDFs, or supplier quotes in seconds', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  { icon: FileText, title: 'Visual Page Editor', desc: 'Gamma-style drag-and-drop editor with live images, direct editing, and instant PDF export', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { icon: Users, title: 'Multi-User CRM', desc: 'Manage leads, pipeline stages, follow-ups, and team assignments in one place', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  { icon: MessageSquare, title: 'AI Assistant', desc: 'Chat with AI to modify itineraries — change images, rewrite content, add pages instantly', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  { icon: BarChart3, title: 'Reports & Analytics', desc: 'Excel exports, revenue tracking, lead analytics, and booking reports', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  { icon: Shield, title: 'Multi-Tenant Security', desc: 'Complete data isolation between agencies with role-based access control', color: 'text-rose-500', bg: 'bg-rose-500/10' },
];

const CRM_FEATURES = [
  'Full CRM access',
  'Leads & customer management',
  'Enquiries & follow-up management',
  'Quotation & itinerary management',
  'AI itinerary builder with visual editor',
  'Branded PDF export',
  'Unsplash & Pixabay image search',
  'Autosave & revision history',
  'Agency branding & preferences',
];

const formatPrice = (paise) => new Intl.NumberFormat('en-IN').format(paise / 100);

export default function LandingPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get('/credits/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const plans = config?.plans || {};
  const packages = config?.packages || {};
  const schedule = config?.schedule || {};

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
              <Plane className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-slate-900 dark:text-white">VoyageOS</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-600 dark:text-slate-400">
            <a href="#features" className="hover:text-slate-900 dark:hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-slate-900 dark:hover:text-white transition-colors">Pricing</a>
            <a href="#credits" className="hover:text-slate-900 dark:hover:text-white transition-colors">AI Credits</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-sm font-medium" onClick={() => navigate('/login')} data-testid="landing-login-btn">
              Log In
            </Button>
            <Button className="text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white gap-1.5 shadow-lg shadow-blue-500/20"
              onClick={() => navigate('/signup')} data-testid="landing-signup-btn">
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 via-transparent to-transparent dark:from-blue-950/20" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-blue-400/10 to-violet-400/10 blur-3xl" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium mb-6 border border-emerald-100 dark:border-emerald-800">
            <Gift className="w-3.5 h-3.5" /> Festive Offer — 15 Day Free Trial for New Users
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-900 dark:text-white leading-tight mb-6">
            The CRM that turns
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> travel agencies </span>
            into powerhouses
          </h1>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Generate AI itineraries, manage leads, automate follow-ups, and close more deals.
            Built for travel agents who want to wow their clients.
          </p>
          <div className="flex items-center justify-center gap-4 mb-12">
            <Button size="lg" className="text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white gap-2 px-8 h-12 shadow-xl shadow-blue-500/25"
              onClick={() => navigate('/signup')} data-testid="hero-signup-btn">
              Start 15-Day Free Trial <ArrowRight className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="text-base h-12 px-8" onClick={() => navigate('/login')}>
              Log In
            </Button>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm text-slate-500 dark:text-slate-500">
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> No credit card required</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> 15-day free trial</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> 500 AI credits included</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white mb-3">Everything you need to run a travel agency</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xl mx-auto">From lead capture to itinerary delivery — one platform, zero chaos.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(f => (
              <div key={f.title} className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className={`w-11 h-11 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon className={`w-5 h-5 ${f.color}`} />
                </div>
                <h3 className="font-heading font-bold text-base text-slate-900 dark:text-white mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white mb-3">How VoyageOS works</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', icon: Users, title: 'Add Leads', desc: 'Import from CSV/Excel or add manually' },
              { step: '2', icon: Sparkles, title: 'AI Generates', desc: 'Paste trip details, AI creates the itinerary' },
              { step: '3', icon: FileText, title: 'Edit & Brand', desc: 'Visual editor with your logo and images' },
              { step: '4', icon: Globe, title: 'Send & Close', desc: 'Email PDF or share via WhatsApp' },
            ].map((s, i) => (
              <div key={s.step} className="text-center relative">
                {i < 3 && <ChevronRight className="hidden md:block absolute -right-5 top-8 w-5 h-5 text-slate-300" />}
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/20">
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-sm text-slate-900 dark:text-white mb-1">{s.title}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white mb-3">Simple, transparent pricing</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">Start with a 15-day free trial, upgrade when you're ready. All prices in INR + applicable taxes.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="pricing-plans">
            {/* Starter */}
            <div className="rounded-2xl p-7 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg transition-shadow" data-testid="plan-starter">
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Zap className="w-4.5 h-4.5 text-blue-600" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white">Starter</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">Solo travel agents and small agencies</p>
              <div className="mb-5">
                <span className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
                  {plans.starter ? `₹${formatPrice(plans.starter.price)}` : '₹799'}
                </span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                <Coins className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">500 AI credits/month</span>
              </div>
              <ul className="space-y-2 mb-6">
                {CRM_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {f}
                  </li>
                ))}
                <li className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <Users className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" /> Up to <strong className="mx-0.5">3</strong> users
                </li>
              </ul>
              <p className="text-[10px] text-slate-400 mb-4">~15 standard itinerary generations at 30 credits each</p>
              <Button className="w-full gradient-blue hover:opacity-90 text-white shadow-md shadow-blue-500/20" onClick={() => navigate('/signup')} data-testid="starter-cta">
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Pro */}
            <div className="rounded-2xl p-7 border-2 border-indigo-500 bg-white dark:bg-slate-900 shadow-xl shadow-indigo-500/10 ring-1 ring-indigo-500/20 relative hover:shadow-2xl transition-shadow" data-testid="plan-pro">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-[10px] px-3 py-0.5 font-bold uppercase tracking-wider shadow-md">Recommended</Badge>
              </div>
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Star className="w-4.5 h-4.5 text-indigo-600" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white">Pro</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">Active agencies creating itineraries regularly</p>
              <div className="mb-5">
                <span className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
                  {plans.pro ? `₹${formatPrice(plans.pro.price)}` : '₹1,499'}
                </span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
                <Coins className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">1,500 AI credits/month</span>
              </div>
              <ul className="space-y-2 mb-6">
                {CRM_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {f}
                  </li>
                ))}
                <li className="flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                  <Users className="w-3.5 h-3.5 flex-shrink-0" /> Up to <strong className="mx-0.5">10</strong> users
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                  <Brain className="w-3.5 h-3.5 flex-shrink-0" /> Higher AI usage allowance
                </li>
              </ul>
              <p className="text-[10px] text-slate-400 mb-4">~45 standard itinerary generations at 30 credits each</p>
              <Button className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-md shadow-indigo-500/20" onClick={() => navigate('/signup')} data-testid="pro-cta">
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* AI Credit Packages */}
      <section id="credits" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white mb-3">Need more AI credits?</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-lg mx-auto">
              Top up your AI credits anytime. Purchased credits are valid for 12 months and are separate from your monthly allowance.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-3xl mx-auto" data-testid="credit-packages">
            {[
              { id: 'mini', icon: Coins, color: 'blue', name: packages.mini?.name || 'Mini', credits: packages.mini?.credits || 200, price: packages.mini?.price || 9900 },
              { id: 'growth', icon: Zap, color: 'violet', name: packages.growth?.name || 'Growth', credits: packages.growth?.credits || 600, price: packages.growth?.price || 24900 },
              { id: 'power', icon: Sparkles, color: 'amber', name: packages.power?.name || 'Power', credits: packages.power?.credits || 1400, price: packages.power?.price || 49900 },
            ].map(pkg => (
              <div key={pkg.id}
                className={`rounded-2xl p-6 border bg-white dark:bg-slate-900 hover:shadow-lg hover:-translate-y-0.5 transition-all ${
                  pkg.id === 'growth' ? 'border-violet-300 dark:border-violet-700 ring-1 ring-violet-200' : 'border-slate-200 dark:border-slate-700'
                }`}
                data-testid={`credit-pkg-${pkg.id}`}
              >
                {pkg.id === 'growth' && (
                  <Badge className="bg-violet-100 text-violet-700 text-[10px] mb-3">Best Value</Badge>
                )}
                <div className={`w-10 h-10 rounded-xl bg-${pkg.color}-100 dark:bg-${pkg.color}-900/30 flex items-center justify-center mb-3`}>
                  <pkg.icon className={`w-5 h-5 text-${pkg.color}-600`} />
                </div>
                <h3 className="font-heading font-bold text-base text-slate-900 dark:text-white">{pkg.name}</h3>
                <p className="text-2xl font-heading font-bold text-slate-900 dark:text-white mt-2">
                  {pkg.credits.toLocaleString('en-IN')} <span className="text-sm font-normal text-slate-500">credits</span>
                </p>
                <p className="text-lg font-semibold text-slate-700 dark:text-slate-300 mt-1">
                  ₹{formatPrice(pkg.price)}
                </p>
                <p className="text-[10px] text-slate-400 mt-1 mb-4">₹{(pkg.price / 100 / pkg.credits).toFixed(2)}/credit &middot; Valid 12 months</p>
                <Button variant="outline" className="w-full" onClick={() => navigate('/signup')} data-testid={`credit-buy-${pkg.id}`}>
                  Buy Credits
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credit Schedule */}
      <section className="py-16 px-6 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-xl font-heading font-bold text-slate-900 dark:text-white mb-2">AI Credit Usage</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">Transparent pricing per AI operation. Stock image searches and manual editing are always free.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-5 py-3 font-medium text-slate-600 dark:text-slate-400">AI Operation</th>
                  <th className="text-right px-5 py-3 font-medium text-slate-600 dark:text-slate-400">Credits</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(schedule).map(([key, val]) => (
                  <tr key={key} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                    <td className="px-5 py-2.5 text-slate-700 dark:text-slate-300">{val.label}</td>
                    <td className="px-5 py-2.5 text-right">
                      {val.cost === 0 ? (
                        <span className="text-emerald-600 font-medium">Free</span>
                      ) : (
                        <span className="font-semibold text-slate-900 dark:text-white">{val.cost}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white mb-4">Ready to transform your travel business?</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-8">Join travel agencies already using VoyageOS to win more clients.</p>
          <Button size="lg" className="text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white gap-2 px-8 h-12 shadow-xl shadow-blue-500/25"
            onClick={() => navigate('/signup')}>
            Start Your 15-Day Free Trial <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                <Plane className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-base font-bold text-slate-900 dark:text-white">VoyageOS</span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 text-sm text-slate-600 dark:text-slate-400">
              <a href="mailto:hello@voyageos.online" className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="footer-email">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                hello@voyageos.online
              </a>
              <a href="tel:+916353335663" className="flex items-center gap-2 hover:text-slate-900 dark:hover:text-white transition-colors" data-testid="footer-phone">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                +91 6353335663
              </a>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-400 flex items-center justify-between">
            <p>Built for travel agencies that refuse to be ordinary.</p>
            <p>&copy; {new Date().getFullYear()} VoyageOS. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
